import * as React from "react";
import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {assetAllocationSelectedDate} from "./PortfolioMapSelectors";
import {IPlanTreeNode} from "../../model/Portfolio.model";
import {ISelectValue} from "../common/Select.component";
import {setAssetAllocationDateAction} from "./PortfolioActions";
import {NewSelectComponent} from "../common/NewSelect.component";
import {AssetAllocationTreeTable} from "./AssetAllocationTreeTable";
import {AssetAllocationDonutChart} from "./AssetAllocationDonutChart";
import {
    getDateOptions,
    getPtcOfTotalPriorPeriod,
    getTotalNode,
    isDcPlan
} from "./portfolioUtil";
import {formatCurrency} from "../../utils/numberUtil";
import {getProductRating} from "../product/ProductMapper";
import {TargetAllocationChart} from "./TargetAllocationChart";
import {RaisedButton} from "../common/RaisedButton";
import {prominentButtonStyle} from "../common/buttonStyles";
import {navigateTo} from "../../../navigateTo";
import {hasConsultantLevelAccess} from "../../utils/sessionUtil";
import {StyledToggle} from "../common/material-ui-core/Toggle.component";
import planApi from "../../api/PlanApi";
import {requestPlanSettings} from "../root/rootActions";
import {hasClientModelPortfolioAccess} from "../../../mainReducerMapSelectors";
import {useNavigate} from "react-router-dom";
import {IApplicationRootState} from "../../../applicationState";
import {AnyThunkDispatch} from "../../../commonTypes";

export interface IAssetAllocationTabProps {
    planTreeNodes: IPlanTreeNode[];
    planType: string;
    showModelPortfolioComparison: boolean;
}

export const PORTFOLIO_COMPARISON_TITLE = "Portfolio Comparison";
export const PORTFOLIO_COMPARISON_TEXT = "Curious how your current investment mix aligns with our strategic thinking? Explore our model portfolios and evaluate potential enhancements to your asset allocation.";
export const CLIENT_MODEL_PORTFOLIO_TOGGLE_TEXT = "Show Model Portfolio for Client Users";
export const AssetAllocationTab: React.FunctionComponent<IAssetAllocationTabProps> = (props) => {
    const navigate = useNavigate();

    const dateOptionFromStore =
        useSelector((state: IApplicationRootState) => assetAllocationSelectedDate(state));
    const clientModelPortfolioAccessFromStore =
        useSelector((state: IApplicationRootState) => hasClientModelPortfolioAccess(state));
    const [nodes, setNodes] = React.useState<IPlanTreeNode[]>([]);
    const [currentDateOption, setCurrentDateOption] = useState<ISelectValue | undefined>(undefined);
    const [clientModelPortfolioAccess, setClientModelPortfolioAccess]
        = useState<boolean>(clientModelPortfolioAccessFromStore);
    const [dateOptions, setDateOptions] = useState<ISelectValue[]>([]);

    const dispatch: AnyThunkDispatch = useDispatch();

    const setDates = (nodes: IPlanTreeNode[]) => {
        const dateOptions = getDateOptions(nodes);
        setDateOptions(dateOptions);
        setCurrentDateOption(dateOptionFromStore ? dateOptionFromStore : dateOptions[0]);
    };


    const getNodes = (flatNodes: IPlanTreeNode[]) => {
        const ratedNodes = flatNodes.map((node: IPlanTreeNode): IPlanTreeNode => {
            return {
                ...node,
                rating: node.rating ? getProductRating(node.rating, node.ifaRating) : "",
                priorMarketValuePctOfTotal: getPtcOfTotalPriorPeriod(node, flatNodes)
            };
        });
        setNodes(ratedNodes);
    };

    useEffect(() => {
        setDates(props.planTreeNodes);
        getNodes(props.planTreeNodes);
    }, [props.planTreeNodes]);

    useEffect(() => {
        if (currentDateOption) dispatch(setAssetAllocationDateAction(currentDateOption));
    }, [currentDateOption]);


    const handleDateChange = (e: any) => {
        setCurrentDateOption(dateOptions.find((date) => date.id === e.target.value));
    };

    const renderCompareButton = () => {
        return <div className="home-cpr-portfolio__client-reporting-link">
            <RaisedButton
                className="client-reporting-link-button"
                style={prominentButtonStyle}
                primary={true}
                onClick={() => navigateTo("/model-portfolio", navigate)}>
                Compare
            </RaisedButton>
        </div>;
    };

    const updateClientModelPortfolioAccess = (clientModelPortfolioAccess: boolean) => {
        setClientModelPortfolioAccess(clientModelPortfolioAccess);
        planApi.updatePlanClientModelPortfolioEnabled(clientModelPortfolioAccess)
        .then(() => {
            dispatch(requestPlanSettings());
        });
    };

    const renderClientModelPortfolioToggle = () => {
        return hasConsultantLevelAccess()
            ? <span className="asset-allocation-tab__model-portfolio-toggle"
                   data-testid="asset-allocation-tab__model-portfolio-toggle">
                <StyledToggle
                    toggleState={clientModelPortfolioAccess}
                    setToggle={updateClientModelPortfolioAccess}
                    label={CLIENT_MODEL_PORTFOLIO_TOGGLE_TEXT}
                    labelPlacement="start"/>
            </span>
            : null;
    };

    const renderPortfolioComparison = () => {
        if (!props.showModelPortfolioComparison) {
            return null;
        }

        return <div className="asset-allocation-tab__portfolio-comparison"
                    data-testid="asset-allocation-tab__portfolio-comparison">
            <div className="asset-allocation-tab__portfolio-comparison-label">
                {PORTFOLIO_COMPARISON_TITLE}
                {renderClientModelPortfolioToggle()}
            </div>
            <div className={"asset-allocation-tab__text-comparison-button-container"}>
                <div className="asset-allocation-tab__portfolio-comparison-text">
                    {PORTFOLIO_COMPARISON_TEXT}
                </div>
                {renderCompareButton()}
            </div>
        </div>;
    };

    const renderDateSelection = () => {
        return <div className="asset-allocation-tab__date-selection" data-testid="asset-allocation-tab__date-selection">
            <div className="asset-allocation-tab__date-label">As of:</div>
            <NewSelectComponent
                id={"asset-allocation-tab__dates"}
                values={dateOptions}
                selected={currentDateOption?.id}
                width={200}
                handleChange={handleDateChange}
                classNameSuffix="filter-menu_asset-allocation-tab-dates"
            />
        </div>;
    };

    const renderPlanTreeTable = () => {
        return currentDateOption &&
            <AssetAllocationTreeTable selectedDate={currentDateOption!} datedPlanNodes={nodes}/>;
    };

    const getChartContainerClass = (index: number) => {
        return index === 0 ? "asset-allocation-tab__chart-left" : "asset-allocation-tab__chart-right";
    };

    const renderAssetAllocationDonutCharts = () => {
        const selectedDateIndex = dateOptions.findIndex(date => date.id === currentDateOption?.id);

        return dateOptions
            .filter((_, index) => index === selectedDateIndex || index === selectedDateIndex + 1)
            .map((date, index) => (
                <div key={index} className={getChartContainerClass(index)}
                     data-testid="asset-allocation-tab__chart-container">
                    <AssetAllocationDonutChart selectedDate={date} dataNodes={nodes} key={`donut-chart-${index}`}
                                               titlePrefix={index === 0 ? "Total" : "Prior Period"}/>
                </div>
            ));
    };

    const renderTotalAssets = () => {
        const totalNode = getTotalNode(nodes, currentDateOption?.id as string) as IPlanTreeNode | undefined;
        return <div data-testid="asset-allocation-tab__total-assets" className="asset-allocation-tab__total-assets">
            {totalNode === undefined ? "" : formatCurrency(totalNode!.marketValue, 0)}
        </div>;
    };

    const renderContent = () => {
        return <div className="asset-allocation-tab__container"
                    data-testid={"asset-allocation-tab__container"}>
            {renderPortfolioComparison()}
            {renderDateSelection()}
            <div className={"asset-allocation-tab__charts-container"}>
                <div className="asset-allocation-tab__charts">{renderAssetAllocationDonutCharts()}</div>
            </div>
            {!isDcPlan(props.planType) && <TargetAllocationChart/>}
            <div className="asset-allocation-tab__table-container">
                <div className="asset-allocation-tab__title-selection">
                    <div>
                        <div className="asset-allocation-tab__title-label">
                            <h3>Total Market Value</h3>
                            <p className="asset-allocation-tab__title-sub-label"
                               data-testid="asset-allocation-tab__title-sub-label">
                                {`as of ${currentDateOption?.name}`}
                            </p>
                        </div>
                        {renderTotalAssets()}
                    </div>
                </div>
                {renderPlanTreeTable()}
            </div>
        </div>;
    };

    return renderContent();
};